import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { errorMessage, put } from "../api";
import { Alert, Button, cx } from "./ui";

/**
 * A signature for school and work notes: drawn with a finger or stylus, or a PNG uploaded (a scan).
 * Either way it's trimmed to the ink and kept as a PNG data URL no bigger than 600×200.
 */
export function SignatureInput({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const [mode, setMode] = useState<"draw" | "upload">("draw");
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1" role="group" aria-label="Signature">
        {(
          [
            ["draw", "Draw it"],
            ["upload", "Upload a PNG"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            aria-pressed={mode === k}
            onClick={() => (setMode(k), onChange(null))}
            className={cx("rounded-lg px-3 py-1.5 text-sm font-semibold", mode === k ? "bg-brand-wash text-brand-deep" : "text-muted hover:bg-mist hover:text-ink")}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === "draw" ? <DrawPad onChange={onChange} /> : <UploadPng onChange={onChange} />}
    </div>
  );
}

/** Crop to the ink (non-transparent pixels) and fit within 600×200. */
function trimmed(src: HTMLCanvasElement | HTMLImageElement): string | null {
  const w = src instanceof HTMLCanvasElement ? src.width : src.naturalWidth;
  const h = src instanceof HTMLCanvasElement ? src.height : src.naturalHeight;
  const work = document.createElement("canvas");
  work.width = w;
  work.height = h;
  const ctx = work.getContext("2d")!;
  ctx.drawImage(src, 0, 0);
  const px = ctx.getImageData(0, 0, w, h).data;
  let [x0, y0, x1, y1] = [w, h, -1, -1];
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      // Ink: not transparent, and not near-white (a scan on white paper).
      if (px[i + 3]! > 20 && px[i]! + px[i + 1]! + px[i + 2]! < 690) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  if (x1 < 0) return null;
  const pad = 6;
  [x0, y0, x1, y1] = [Math.max(0, x0 - pad), Math.max(0, y0 - pad), Math.min(w - 1, x1 + pad), Math.min(h - 1, y1 + pad)];
  const cw = x1 - x0 + 1;
  const ch = y1 - y0 + 1;
  const scale = Math.min(1, 600 / cw, 200 / ch);
  const out = document.createElement("canvas");
  out.width = Math.round(cw * scale);
  out.height = Math.round(ch * scale);
  out.getContext("2d")!.drawImage(work, x0, y0, cw, ch, 0, 0, out.width, out.height);
  return out.toDataURL("image/png");
}

/** Draw with a finger, stylus, or mouse; trimmed to the ink. */
export function DrawPad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current!;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext("2d")!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
  }, []);

  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange(null);
  };
  return (
    <div className="flex flex-col gap-1">
      <canvas
        ref={ref}
        aria-label="Sign here"
        className="h-36 w-full max-w-lg touch-none rounded-[var(--radius-control)] border border-line bg-white"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          const ctx = ref.current!.getContext("2d")!;
          const [x, y] = at(e);
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + 0.1, y + 0.1);
          ctx.stroke();
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = ref.current!.getContext("2d")!;
          const [x, y] = at(e);
          ctx.lineTo(x, y);
          ctx.stroke();
        }}
        onPointerUp={() => {
          if (!drawing.current) return;
          drawing.current = false;
          setEmpty(false);
          onChange(trimmed(ref.current!));
        }}
      />
      <div className="flex max-w-lg justify-between text-xs text-muted">
        <span>{empty ? "Sign in the box with a finger, stylus, or mouse." : "Signed."}</span>
        <button type="button" className="font-semibold text-brand" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
}

function UploadPng({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const read = (f: File | undefined) => {
    setError(null);
    setPreview(null);
    onChange(null);
    if (!f) return;
    if (f.type !== "image/png") return setError("Pick a PNG image.");
    const img = new Image();
    img.onload = () => {
      const url = trimmed(img);
      URL.revokeObjectURL(img.src);
      if (!url) return setError("That image looks blank.");
      setPreview(url);
      onChange(url);
    };
    img.onerror = () => setError("That image couldn't be read.");
    img.src = URL.createObjectURL(f);
  };
  return (
    <div className="flex flex-col gap-2">
      <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-line bg-white px-4 py-2 font-semibold hover:border-brand">
        Choose a PNG
        <input type="file" accept="image/png" className="sr-only" onChange={(e) => read(e.target.files?.[0])} />
      </label>
      <span className="text-xs text-muted">A scan or photo of the signature on white (or a transparent background) works best.</span>
      {error && <span className="text-sm text-danger">{error}</span>}
      {preview && <SignatureImage src={preview} />}
    </div>
  );
}

export function SignatureImage({ src }: { src: string }) {
  return <img src={src} alt="Signature" className="h-14 w-auto max-w-xs rounded border border-mist bg-white object-contain p-1" />;
}

/** A saved signature (a person's own, or set by a manager): shown, replaced, or removed. */
export function SavedSignature({ url, signature, onSaved }: { url: string; signature: string | null; onSaved: (s: string | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (s: string | null) => put<{ signature: string | null }>(url, { signature: s }),
    onSuccess: (r) => (onSaved(r.signature), setEditing(false), setNext(null)),
  });
  return (
    <div className="flex flex-col gap-3">
      {!editing && (
        <div className="flex flex-wrap items-center gap-3">
          {signature ? <SignatureImage src={signature} /> : <span className="text-sm text-muted">No signature saved yet.</span>}
          <Button onClick={() => setEditing(true)}>{signature ? "Replace" : "Add signature"}</Button>
          {signature && (
            <Button variant="quiet" busy={save.isPending} onClick={() => window.confirm("Remove the saved signature?") && save.mutate(null)}>
              Remove
            </Button>
          )}
        </div>
      )}
      {editing && (
        <>
          <SignatureInput onChange={setNext} />
          <div className="flex gap-2">
            <Button variant="primary" disabled={!next} busy={save.isPending} onClick={() => save.mutate(next)}>
              Save signature
            </Button>
            <Button variant="quiet" onClick={() => (setEditing(false), setNext(null))}>
              Cancel
            </Button>
          </div>
        </>
      )}
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
    </div>
  );
}
